import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import BoughtTogetherModal from '../components/BoughtTogetherModal';
import AccessoriesModal from '../components/AccessoriesModal';
import { toast } from '../components/Toast';
import ImageGallery from '../components/ImageGallery';
import ProductInfo from '../components/ProductInfo';
import Specifications, { hasRichSpecifications } from '../components/Specifications';
import RelatedProducts from '../components/RelatedProducts';
import StickyAddToCart from '../components/StickyAddToCart';
import CompatibleAccessories from '../components/CompatibleAccessories';
import { getCartRecommendations } from '../api/product.api';
import { CartContext } from '../context/CartContext';
import { WishlistContext } from '../context/WishlistContext';
import { AuthContext } from '../context/AuthContext';
import { ENDPOINTS, resolveAssetUrl, resolveHtmlAssetUrls, canonicalUrl, productPath, categoryPath, resolveSeoTitle } from '../config';
import { isPlaceholderColor } from '../utils/placeholderColor';

/**
 * Get full image URLs from a variant's variantImage array.
 * Sorts so isPrimary image comes first.
 */
function getVariantImages(variant) {
    if (!variant?.variantImage || variant.variantImage.length === 0) return [];
    const sorted = [...variant.variantImage].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
    // Stored paths use Windows backslashes (e.g. "uploads\variants\x.webp"). <img>
    // tolerates them, but CSS url() treats "\" as an escape char and breaks (zoom
    // preview). Normalise to forward slashes so the URL works everywhere.
    return sorted.map((img) => resolveAssetUrl(img.path));
}

/**
 * Derive unique selectable colors from all variants.
 */
function deriveUniqueColors(variants) {
    const colorMap = new Map();
    for (const v of variants) {
        if (v.colorId && !isPlaceholderColor(v.colorId) && !colorMap.has(v.colorId._id)) {
            colorMap.set(v.colorId._id, v.colorId);
        }
    }
    return [...colorMap.values()];
}

/**
 * Derive attribute options from variants.
 * Returns: { [attributeSlug]: { name, slug, values: [{ _id, name, slug }] } }
 */
function deriveAttributeOptions(variants) {
    const attrMap = {};

    for (const v of variants) {
        for (const va of (v.variantAttributes || [])) {
            const attrSlug = va.attributeId?.slug;
            if (!attrSlug) continue;

            if (!attrMap[attrSlug]) {
                attrMap[attrSlug] = {
                    name: va.attributeId.name,
                    slug: attrSlug,
                    valuesMap: new Map(),
                };
            }

            const valId = va.valueId?._id;
            if (valId && !attrMap[attrSlug].valuesMap.has(valId)) {
                attrMap[attrSlug].valuesMap.set(valId, {
                    _id: valId,
                    name: va.valueId.name,
                    slug: va.valueId.slug,
                });
            }
        }
    }

    const result = {};
    for (const [slug, data] of Object.entries(attrMap)) {
        result[slug] = {
            name: data.name,
            slug: data.slug,
            values: [...data.valuesMap.values()],
        };
    }
    return result;
}

/**
 * Find a variant matching the given color and attribute selections.
 * Returns the best match or null.
 */
function sameId(a, b) {
    if (a == null || b == null) return false;
    return String(a?._id || a) === String(b?._id || b);
}

function variantHasAttr(variant, attrSlug, valueId) {
    return (variant.variantAttributes || []).some(
        (a) => a.attributeId?.slug === attrSlug && sameId(a.valueId, valueId)
    );
}

function variantMatchesAttrs(variant, attrs = {}) {
    for (const [attrSlug, valueId] of Object.entries(attrs)) {
        if (!valueId) continue;
        if (!variantHasAttr(variant, attrSlug, valueId)) return false;
    }
    return true;
}

function attrsFromVariant(variant) {
    const attrs = {};
    for (const va of variant?.variantAttributes || []) {
        if (va.attributeId?.slug && va.valueId?._id) {
            attrs[va.attributeId.slug] = String(va.valueId._id);
        }
    }
    return attrs;
}

function applyVariantSelection(variant, setSelectedColorId, setSelectedAttrs) {
    if (!variant) return;
    setSelectedColorId(variant.colorId?._id ? String(variant.colorId._id) : null);
    setSelectedAttrs(attrsFromVariant(variant));
}

function findMatchingVariant(variants, colorId, selectedAttrs) {
    // 1. Exact: color + all attrs
    const exactMatch = variants.find(
        (v) =>
            (!colorId || sameId(v.colorId, colorId)) &&
            variantMatchesAttrs(v, selectedAttrs)
    );
    if (exactMatch) return exactMatch;

    // 2. Attrs only (switch color if needed)
    const attrOnly = variants.find((v) => variantMatchesAttrs(v, selectedAttrs));
    if (attrOnly) return attrOnly;

    // 3. Color only
    if (colorId) {
        const colorMatch = variants.find((v) => sameId(v.colorId, colorId));
        if (colorMatch) return colorMatch;
    }

    return variants[0] || null;
}

/** True when some active variant has this value with the other current attrs. */
function isAttrValueCompatible(variants, selectedAttrs, attrSlug, valueId) {
    const trial = { ...selectedAttrs, [attrSlug]: String(valueId) };
    return variants.some((v) => variantMatchesAttrs(v, trial));
}

/**
 * Resolve a real SKU after clicking an attribute value.
 * Never leaves the UI on a fake cartesian combo.
 */
function findVariantForAttrClick(variants, colorId, selectedAttrs, attrSlug, valueId) {
    const nextAttrs = { ...selectedAttrs, [attrSlug]: String(valueId) };

    const exactColor = variants.find(
        (v) =>
            (!colorId || sameId(v.colorId, colorId)) &&
            variantMatchesAttrs(v, nextAttrs)
    );
    if (exactColor) return exactColor;

    const attrsAnyColor = variants.find((v) => variantMatchesAttrs(v, nextAttrs));
    if (attrsAnyColor) return attrsAnyColor;

    // Drop other attrs — e.g. 1GB/16GB → click 32GB → land on 2GB/32GB
    const withValue = variants.filter((v) => variantHasAttr(v, attrSlug, valueId));
    if (colorId) {
        const sameColor = withValue.find((v) => sameId(v.colorId, colorId));
        if (sameColor) return sameColor;
    }
    return withValue[0] || null;
}

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useContext(CartContext);
    const { isAuthenticated } = useContext(AuthContext);
    const { addToWishlist, removeFromWishlist, isInWishlist } = useContext(WishlistContext);
    const [product, setProduct] = useState(null);
    const [variants, setVariants] = useState([]);
    const [boughtTogether, setBoughtTogether] = useState([]);
    const [compatibleAccessories, setCompatibleAccessories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [addingToCart, setAddingToCart] = useState(false);
    const [showBoughtTogetherModal, setShowBoughtTogetherModal] = useState(false);
    const [showAccessoriesModal, setShowAccessoriesModal] = useState(false);

    // Selection state
    const [selectedColorId, setSelectedColorId] = useState(null);
    const [selectedAttrs, setSelectedAttrs] = useState({});
    // Variant the requested URL addresses (legacy per-variant slugs), if any.
    const [initialVariantId, setInitialVariantId] = useState(null);
    const [detailTab, setDetailTab] = useState('specifications');

    // Fetch product — skip re-fetch when we only navigated from parent slug →
    // variant slug for the same product already in memory (avoids double load).
    useEffect(() => {
        const slugOnLoadedProduct =
            product &&
            variants.length > 0 &&
            (slug === product.slug ||
                variants.some(
                    (v) =>
                        v.slug === slug ||
                        (Array.isArray(v.previousSlugs) && v.previousSlugs.includes(slug))
                ));

        if (slugOnLoadedProduct) {
            const match =
                variants.find((v) => v.slug === slug) ||
                variants.find(
                    (v) =>
                        Array.isArray(v.previousSlugs) && v.previousSlugs.includes(slug)
                );
            if (match) setInitialVariantId(match._id || null);
            setLoading(false);
            return;
        }

        let cancelled = false;

        const fetchProduct = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = await fetch(`${ENDPOINTS.PRODUCTS.PUBLIC}/${slug}`);
                const data = await response.json();
                if (cancelled) return;

                if (data?.isOk && data?.data?.product) {
                    const nextVariants = (data.data.variants || []).filter(
                        (v) => v.isActive !== false
                    );
                    setProduct(data.data.product);
                    setVariants(nextVariants);
                    setBoughtTogether(data.data.boughtTogether || []);
                    setInitialVariantId(data.data.selectedVariantId || null);
                    // Parent/legacy slug → variant URL via Router so Seo canonical matches.
                    const canonicalSlug = data.data.canonicalSlug;
                    if (canonicalSlug && canonicalSlug !== slug) {
                        navigate(productPath(canonicalSlug), { replace: true });
                    }
                } else {
                    setError(true);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('Error fetching product:', err);
                setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchProduct();
        setQuantity(1);
        setSelectedColorId(null);
        setSelectedAttrs({});

        return () => {
            cancelled = true;
        };
    }, [slug, navigate]); // product/variants intentionally omitted — skip-refetch guard reads them

    // Auto-select from the URL slug first, then API hint, then first purchasable.
    useEffect(() => {
        if (variants.length === 0) return;

        const isBuyable = (v) => {
            if (!v) return false;
            if (typeof v.purchasable === 'boolean') return v.purchasable;
            if (typeof v.inStock === 'boolean') return v.inStock;
            return !v.forceOutOfStock;
        };

        const bySlug =
            variants.find((v) => v.slug === slug) ||
            variants.find(
                (v) =>
                    Array.isArray(v.previousSlugs) && v.previousSlugs.includes(slug)
            );
        const byHint =
            initialVariantId &&
            variants.find((v) => sameId(v._id, initialVariantId));
        const firstBuyable = variants.find(isBuyable);

        // URL wins (including intentional OOS SKUs). Otherwise prefer buyable.
        const initial = bySlug || byHint || firstBuyable || variants[0];
        setSelectedColorId(initial.colorId?._id ? String(initial.colorId._id) : null);
        setSelectedAttrs(attrsFromVariant(initial));
    }, [variants, initialVariantId, slug]);

    // Fetch compatible accessories for parent (non-accessory) products.
    useEffect(() => {
        if (!product?._id || product.isAccessory) {
            setCompatibleAccessories([]);
            return;
        }
        let cancelled = false;
        getCartRecommendations([product._id], 12)
            .then((res) => {
                if (!cancelled && res?.data?.isOk) {
                    setCompatibleAccessories(res.data.data || []);
                }
            })
            .catch((err) => console.error('Error fetching compatible accessories:', err));
        return () => {
            cancelled = true;
        };
    }, [product?._id, product?.isAccessory]);

    // Derived data
    const uniqueColors = useMemo(() => deriveUniqueColors(variants), [variants]);
    const attributeOptions = useMemo(() => deriveAttributeOptions(variants), [variants]);

    const selectedVariant = useMemo(
        () => findMatchingVariant(variants, selectedColorId, selectedAttrs),
        [variants, selectedColorId, selectedAttrs]
    );

    // If match fell back (attrs pointed at a missing/invalid combo), realign
    // the UI selection to the variant actually shown (SKU / price / image).
    useEffect(() => {
        if (!selectedVariant) return;
        const matched = attrsFromVariant(selectedVariant);
        const attrKeys = new Set([
            ...Object.keys(selectedAttrs || {}),
            ...Object.keys(matched),
        ]);
        let drift = false;
        for (const key of attrKeys) {
            if (String(selectedAttrs[key] || "") !== String(matched[key] || "")) {
                drift = true;
                break;
            }
        }
        const colorId = selectedVariant.colorId?._id
            ? String(selectedVariant.colorId._id)
            : null;
        if (colorId && String(selectedColorId || "") !== colorId) {
            setSelectedColorId(colorId);
        }
        if (drift) setSelectedAttrs(matched);
    }, [selectedVariant]); // eslint-disable-line react-hooks/exhaustive-deps

    const images = useMemo(() => getVariantImages(selectedVariant), [selectedVariant]);

    const specFirst = useMemo(() => hasRichSpecifications(product), [product]);

    useEffect(() => {
        if (!product) return;
        setDetailTab(specFirst ? 'specifications' : 'description');
    }, [product?._id, specFirst]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handlers — always land on a real active variant (never a fake combo).
    // Navigate to the variant's own slug so the address bar + canonical stay aligned.
    const handleAttrChange = useCallback(
        (attrSlug, valueId) => {
            const matched = findVariantForAttrClick(
                variants,
                selectedColorId,
                selectedAttrs,
                attrSlug,
                valueId
            );
            if (!matched) return;
            applyVariantSelection(matched, setSelectedColorId, setSelectedAttrs);
            if (matched.slug && matched.slug !== slug) {
                navigate(productPath(matched.slug), { replace: true });
            }
        },
        [variants, selectedColorId, selectedAttrs, slug, navigate]
    );

    const handleColorChange = useCallback(
        (colorId) => {
            const id = colorId ? String(colorId) : null;
            if (!id) return;

            const exact = variants.find(
                (v) => sameId(v.colorId, id) && variantMatchesAttrs(v, selectedAttrs)
            );
            const matched =
                exact || variants.find((v) => sameId(v.colorId, id));
            if (!matched) return;
            applyVariantSelection(matched, setSelectedColorId, setSelectedAttrs);
            if (matched.slug && matched.slug !== slug) {
                navigate(productPath(matched.slug), { replace: true });
            }
        },
        [variants, selectedAttrs, slug, navigate]
    );

    // Full SKU pick (phones/tablets combined cards) — always a real variant.
    const handleVariantSelect = useCallback(
        (variant) => {
            if (!variant) return;
            applyVariantSelection(variant, setSelectedColorId, setSelectedAttrs);
            if (variant.slug && variant.slug !== slug) {
                navigate(productPath(variant.slug), { replace: true });
            }
        },
        [slug, navigate]
    );

    const handleAddToCart = useCallback(async () => {
        if (!selectedVariant?._id) {
            toast.error('Please select a variant');
            return;
        }
        try {
            setAddingToCart(true);
            await addToCart(selectedVariant._id, quantity);
            if (boughtTogether.length > 0) {
                setShowBoughtTogetherModal(true);
            } else if (compatibleAccessories.length > 0) {
                setShowAccessoriesModal(true);
            } else {
                toast.success(`${product?.name || 'Product'} added to cart!`);
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to add to cart';
            toast.error(msg);
        } finally {
            setAddingToCart(false);
        }
    }, [selectedVariant, quantity, addToCart, product, boughtTogether, compatibleAccessories]);

    const handleBuyNow = useCallback(async () => {
        if (!selectedVariant?._id) {
            toast.error('Please select a variant');
            return;
        }
        try {
            setAddingToCart(true);
            await addToCart(selectedVariant._id, quantity);
            navigate('/checkout');
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to add to cart';
            toast.error(msg);
        } finally {
            setAddingToCart(false);
        }
    }, [selectedVariant, quantity, addToCart, navigate]);

    const handleToggleWishlist = useCallback(async () => {
        if (!isAuthenticated) {
            toast.info('Please sign in to use wishlist');
            navigate('/login');
            return;
        }
        if (!selectedVariant?._id) return;
        try {
            if (isInWishlist(selectedVariant._id)) {
                await removeFromWishlist(selectedVariant._id);
                toast.success('Removed from wishlist');
            } else {
                await addToWishlist(selectedVariant._id);
                toast.success('Added to wishlist');
            }
        } catch {
            toast.error('Wishlist update failed');
        }
    }, [isAuthenticated, selectedVariant, isInWishlist, removeFromWishlist, addToWishlist, navigate]);

    // Loading
    if (loading) {
        return (
            <div className="pt-[10px] min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading product...</p>
                </div>
            </div>
        );
    }

    // Error / Not found
    if (error || !product) {
        return (
            <div className="pt-[10px] min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <i className="ri-error-warning-line text-5xl text-muted-foreground/40 mb-4 block"></i>
                    <h2 className="text-2xl font-bold text-foreground mb-4">Product Not Found</h2>
                    <p className="text-muted-foreground mb-6">This product may have been removed or doesn't exist.</p>
                    <button
                        onClick={() => navigate('/products')}
                        className="px-6 py-3 bg-primary text-white rounded-lg font-semibold whitespace-nowrap hover:brightness-110 transition-all"
                    >
                        Browse Products
                    </button>
                </div>
            </div>
        );
    }

    const primaryCategory =
        product.categoryIds?.[0] || product.categories?.[0] || null;
    const categoryName = primaryCategory?.name || '';
    const categorySlug = primaryCategory?.slug || '';
    const brandSlug = product.brandId?.slug || product.brand?.slug || '';

    const descriptionHtml = resolveHtmlAssetUrls(product.description || '');

    // SEO meta — per-variant first (each variant is its own indexed page, like
    // the legacy site), then product-level fields, then name/description.
    const variantLabel = (selectedVariant?.variantAttributes || [])
        .map((va) => va.valueId?.name)
        .filter(Boolean)
        .join(' ');
    const displayName = variantLabel ? `${product.name} - ${variantLabel}` : product.name;
    const metaTitle =
        resolveSeoTitle(selectedVariant?.metaTitle, { title: displayName }) ||
        (variantLabel
            ? `${displayName} | FoneXpress Kenya`
            : resolveSeoTitle(product.metaTitle, { title: product.name }) ||
              `${product.name} | FoneXpress Kenya`);
    const plainDescription = (product.description || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const metaDescription =
        selectedVariant?.metaDescription?.trim() ||
        product.metaDescription?.trim() ||
        plainDescription.slice(0, 160);
    const metaKeywords =
        selectedVariant?.metaKeywords?.trim() ||
        product.metaKeywords?.trim() ||
        '';
    // Canonical: the selected variant's own URL (trailing-slash form, matching
    // the URLs indexed from the legacy site).
    const canonicalHref = canonicalUrl(`/product/${selectedVariant?.slug || product.slug}`);

    return (
        <div className="min-h-screen bg-card pt-[10px]">
            <Helmet>
                <title>{metaTitle}</title>
                <link rel="canonical" href={canonicalHref} />
                {metaDescription ? <meta name="description" content={metaDescription} /> : null}
                {metaKeywords ? <meta name="keywords" content={metaKeywords} /> : null}
            </Helmet>
            <div className="max-w-[1400px] mx-auto px-4 py-6">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm mb-5 flex-wrap">
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                    <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                    <Link to="/products" className="text-muted-foreground hover:text-primary transition-colors">Products</Link>
                    {categoryName && (
                        <>
                            <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                            <Link to={categoryPath(categorySlug)} className="text-muted-foreground hover:text-primary transition-colors">
                                {categoryName}
                            </Link>
                        </>
                    )}
                    <i className="ri-arrow-right-s-line text-muted-foreground/60"></i>
                    <span className="text-foreground font-medium">{product.name}</span>
                </nav>

                {/* Main: Gallery + Info */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <ImageGallery
                        images={images}
                        productName={product.name}
                    />
                    <ProductInfo
                        product={product}
                        variants={variants}
                        selectedVariant={selectedVariant}
                        uniqueColors={uniqueColors}
                        attributeOptions={attributeOptions}
                        selectedColorId={selectedColorId}
                        selectedAttrs={selectedAttrs}
                        isAttrValueCompatible={(attrSlug, valueId) =>
                            isAttrValueCompatible(variants, selectedAttrs, attrSlug, valueId)
                        }
                        onColorChange={handleColorChange}
                        onAttrChange={handleAttrChange}
                        onVariantSelect={handleVariantSelect}
                        quantity={quantity}
                        onQuantityChange={setQuantity}
                        onAddToCart={handleAddToCart}
                        onBuyNow={handleBuyNow}
                        onToggleWishlist={handleToggleWishlist}
                        addingToCart={addingToCart}
                        isInWishlist={selectedVariant?._id ? isInWishlist(selectedVariant._id) : false}
                        isAuthenticated={isAuthenticated}
                    />
                </div>

                {/* Spec-first when there are real spec rows; Description-first if only SKU */}
                <section className="mt-8 mb-10">
                    <div
                        className="flex items-center justify-center gap-8 sm:gap-12 mb-5"
                        role="tablist"
                        aria-label="Product details"
                    >
                        {(specFirst
                            ? ['specifications', 'description']
                            : ['description', 'specifications']
                        ).map((tab) => {
                            const selected = detailTab === tab;
                            const label = tab === 'specifications' ? 'Specification' : 'Description';
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    onClick={() => setDetailTab(tab)}
                                    className={`relative pb-2 text-base sm:text-lg transition-colors ${
                                        selected
                                            ? 'font-bold text-foreground'
                                            : 'font-normal text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    {label}
                                    {selected && (
                                        <span className="absolute left-0 right-0 -bottom-0.5 h-0.5 rounded-full bg-primary" />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="rounded-2xl border border-border bg-card px-5 py-6 sm:px-8 sm:py-8 min-h-[120px]">
                        {detailTab === 'specifications' ? (
                            <Specifications
                                product={product}
                                selectedVariant={selectedVariant}
                            />
                        ) : product.description?.trim() ? (
                            <div
                                className="product-description product-description--compact max-w-none text-muted-foreground"
                                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                            />
                        ) : (
                            <p className="text-sm text-muted-foreground text-center">
                                No description available.
                            </p>
                        )}
                    </div>
                </section>

                {/* Compatibility badge for accessory products */}
                {product.isAccessory && Array.isArray(product.compatibleWith) && product.compatibleWith.length > 0 && (
                    <div className="mt-6 bg-card rounded-xl border border-border p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <i className="ri-checkbox-circle-line text-green-600 text-lg"></i>
                            <h3 className="font-semibold text-foreground text-sm">Compatible with your devices</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {product.compatibleWith.map((dev) => (
                                <Link
                                    key={dev._id}
                                    to={`/products/${dev.slug}`}
                                    className="px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 transition-colors"
                                >
                                    {dev.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* Compatible Accessories carousel for parent devices */}
                {compatibleAccessories.length > 0 && (
                    <div className="mt-6">
                        <CompatibleAccessories
                            items={compatibleAccessories}
                            onAddToCart={addToCart}
                        />
                    </div>
                )}

                {/* Related Products */}
                <RelatedProducts
                    currentProductId={product._id}
                    brandSlug={brandSlug}
                    categorySlug={categorySlug}
                />
            </div>

            <StickyAddToCart
                product={product}
                selectedVariant={selectedVariant}
                quantity={quantity}
                onQuantityChange={setQuantity}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
                addingToCart={addingToCart}
            />

            {showBoughtTogetherModal && boughtTogether.length > 0 && (
                <BoughtTogetherModal
                    items={boughtTogether}
                    onAddToCart={addToCart}
                    onClose={() => setShowBoughtTogetherModal(false)}
                    onGoToCart={() => { setShowBoughtTogetherModal(false); navigate('/cart'); }}
                />
            )}

            {showAccessoriesModal && compatibleAccessories.length > 0 && (
                <AccessoriesModal
                    items={compatibleAccessories}
                    productName={product?.name}
                    onAddToCart={addToCart}
                    onClose={() => setShowAccessoriesModal(false)}
                    onGoToCart={() => { setShowAccessoriesModal(false); navigate('/cart'); }}
                />
            )}
        </div>
    );
}
